import React, { createContext, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveTenant } from '../../states/active-tenant.atom';
import HeaderRow from './HeaderRow';
import ErrorFallback from './ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';

interface TenantGuardContextType {
  activeTenant: any;
  isTenantSelected: boolean;
}

const TenantGuardContext = createContext<TenantGuardContextType | undefined>(undefined);

interface TenantGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  showHeader?: boolean;
  headerTitle?: string;
  showBreadcrumbs?: boolean;
}

export const TenantGuard: React.FC<TenantGuardProps> = ({
  children,
  fallback,
  showHeader = false,
  headerTitle,
  showBreadcrumbs = false,
}) => {
  const { t } = useTranslation();
  const { activeTenant } = useActiveTenant();

  const isTenantSelected = !!activeTenant;

  const contextValue: TenantGuardContextType = {
    activeTenant,
    isTenantSelected,
  };

  if (!isTenantSelected) {
    const defaultFallback = (
      <ErrorFallback
        message={t('tenants.noTenantSelected')}
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('tenants.noTenantSelected'))}
        resetErrorBoundary={() => {}}
        showResetButton={false}
      />
    );

    return (
      <TenantGuardContext.Provider value={contextValue}>
        <div>
          {showHeader && headerTitle && (
            <HeaderRow
              title={headerTitle}
              showCreateButton={false}
              showBreadcrumbs={showBreadcrumbs}
            />
          )}
          {fallback || defaultFallback}
        </div>
      </TenantGuardContext.Provider>
    );
  }

  return <TenantGuardContext.Provider value={contextValue}>{children}</TenantGuardContext.Provider>;
};

export const useTenantGuard = (): TenantGuardContextType => {
  const context = useContext(TenantGuardContext);
  if (context === undefined) {
    throw new Error('useTenantGuard must be used within a TenantGuard');
  }
  return context;
};

export default TenantGuard;
